import { Box, Button, Grid, styled, Typography, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import { Search } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import _ from 'underscore';

import { BestMixFeed } from 'views/feed-management/features/best-mix-feeds/types/best-mix-feed.type';
import {
  CustomField as EditCustomField,
  UpdateBestMixFeedDetailsInput,
} from 'views/feed-management/features/best-mix-feeds/types/update-best-mix-feed-details-input.type';
import { Customer } from 'views/feed-management/features/customers/types/customer.type';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import Autocomplete from 'components/autocomplete';
import Breadcrumbs from 'components/breadcrumbs';
import Collapsible from 'components/collapsible';
import Dialog from 'components/dialog';
import Error from 'components/error';
import FlexBox from 'components/flex-box';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';
import TextField from 'components/text-field';
import {
  putBestMixCustomField,
  putFeedManagementBestMixFeedDetails,
} from 'views/feed-management/features/best-mix-feeds/api/update-best-mix-feeds';
import {
  getFeedManagementCustomerProductCustomFields,
  getFeedManagementCustomers,
} from 'views/feed-management/features/customers/api/get-customers';
import FeedManagementTabsHeader from '../../../components/feed-management-tabs-header';
import { getFeedManagementBestMixFeed } from '../api/get-best-mix-feeds';
import { CustomField } from '../types/best-mix-feed-list-item.type';

type ParamTypes = {
  code: 'string';
};

type FormValues = {
  customer: Customer;
  customerPricePerTone: number;
  customfields: CustomField;
  editCustomFields: EditCustomField;
  customfieldValue: number;
  customfieldCode: string;
  customFieldName: string;
};

const WrappedWordCell = styled(ListCell)<{ maxWidth: number }>(({ maxWidth }) => ({
  maxWidth: maxWidth,
  wordWrap: 'break-word',
}));

type Props = {};

const EditBestMixFeed: React.FC<Props> = () => {
  const params = useParams<ParamTypes>();
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const [dialogIndex, setDialogIndex] = useState<number>(0);
  const linkCustomerDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const customFieldsDialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const editCustomFieldsDialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);

  const PAGE_NAME_ID = 'feedManagement.editBestMixFeed';

  // STATE
  const [NutrientsSearchValue, setNutrientsSearchValue] = useState('');
  const [materialsSearchValue, setMaterialsSearchValue] = useState('');

  const assignedCompaniesOptions: HeadCell[] = [
    { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.assignedCompaniesList.nameLabel` }) },
    { id: 'price', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.assignedCompaniesList.priceLabel` }) },
    { id: 'linked', label: '', align: 'center', autoWidth: true },
  ];

  const customFieldsOptions: HeadCell[] = [
    { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.customFieldsList.nameLabel` }) },
    { id: 'value', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.customFieldsList.valueLabel` }) },
    { id: 'remove', label: '', align: 'right', autoWidth: true },
    { id: 'remove', label: '', align: 'right', autoWidth: true },
  ];

  const NutrientsListOptions: HeadCell[] = [
    { id: 'code', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.nutrientsMaterialsList.codeLabel` }) },
    { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.nutrientsMaterialsList.nameLabel` }) },
    { id: 'value', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.nutrientsMaterialsList.valueLabel` }), align: 'right' },
  ];

  // FORMS
  const { handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: {
      customer: undefined,
      customerPricePerTone: 0,
      customfields: undefined,
      customfieldValue: 0,
      customfieldCode: '',
      customFieldName: '',
    },
    shouldUnregister: true,
  });

  // FETCH DATA
  const {
    data: bestMixFeed,
    error,
    isLoading,
    isError,
  } = useGet(['getFeedManagementBestMixFeed', params.code], (context) => getFeedManagementBestMixFeed(String(context.queryKey[1])), {
    enabled: !!params.code,
  });

  const { data: customers } = useGet(['getFeedManagementCustomers'], () => getFeedManagementCustomers());

  const { data: customFields } = useGet(
    ['getFeedManagementCustomerProductCustomFields', account.company?.companyId],
    (context) => getFeedManagementCustomerProductCustomFields(String(context.queryKey[1])),
    {
      enabled: !!account.company?.companyId,
    }
  );

  // POST DATA
  const updateBestMixFeedDetails = usePost<BestMixFeed, UpdateBestMixFeedDetailsInput>(
    (request) => putFeedManagementBestMixFeedDetails(params.code, request),
    (response) => {
      linkCustomerDialogRef.current?.setOpen(false);
      customFieldsDialogRef.current?.setOpen(false);
      queryClient.setQueryData(['getFeedManagementBestMixFeed', params.code], response);
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const updateBestMixCustomField = usePost<AxiosResponse, { productCode: string; customFields: EditCustomField }>(
    (request) => putBestMixCustomField(request.productCode, request.customFields),
    (response) => {
      queryClient.setQueryData(['getFeedManagementBestMixFeed', params.code], response);
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      if (!!response) editCustomFieldsDialogRef.current[dialogIndex].setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // HANDLERS
  const handleUpdateBestMixFeedDetails = (data: FormValues) => {
    const assignedCompaniesDetails = bestMixFeed!.assignedCompaniesDetails?.map((item) => ({
      companyId: item.companyId,
      customerPricePerTone: item.customerPricePerTon,
    }));

    const customFields = bestMixFeed!.customFields?.map((item) => ({
      fieldCode: item.code,
      fieldValue: item.value,
    }));

    updateBestMixFeedDetails.mutate({
      companies: data.customer
        ? [
            ...assignedCompaniesDetails,
            {
              companyId: data.customer.id,
              customerPricePerTone: data.customerPricePerTone,
            },
          ]
        : assignedCompaniesDetails,
      customFields: data.customfields
        ? [...customFields, { fieldCode: data.customfields.code, fieldValue: data.customfieldValue }]
        : customFields,
    });
  };

  const handleUpdateCustomField = (data: FormValues) => {
    updateBestMixCustomField.mutate({
      productCode: String(bestMixFeed?.code),
      customFields: {
        fieldCode: data.customfieldCode,
        fieldValue: Number(data.customfieldValue),
      },
    });
  };

  const handleUnlinkCustomer = (companyId: string) => {
    if (bestMixFeed) {
      updateBestMixFeedDetails.mutate({
        companies: bestMixFeed.assignedCompaniesDetails
          ?.filter((item) => item.companyId !== companyId)
          .map((item) => ({
            companyId: item.companyId,
            customerPricePerTone: item.customerPricePerTon,
          })),
        customFields: bestMixFeed.customFields.map((item) => ({
          fieldCode: item.code,
          fieldValue: item.value,
        })),
      });
    }
  };

  const handleRemoveCustomField = (code: string) => {
    if (bestMixFeed) {
      updateBestMixFeedDetails.mutate({
        companies: bestMixFeed.assignedCompaniesDetails.map((item) => ({
          companyId: item.companyId,
          customerPricePerTone: item.customerPricePerTon,
        })),
        customFields: bestMixFeed.customFields
          .filter((item) => item.code !== code)
          .map((item) => ({ fieldCode: item.code, fieldValue: item.value })),
      });
    }
  };

  // RENDERS

  return isError ? (
    <Error error={error} />
  ) : !bestMixFeed || isLoading ? (
    <LoadingSpinner />
  ) : (
    <>
      <FeedManagementTabsHeader
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: bestMixFeed.description,
                url: bestMixFeed.code,
              },
            ]}
          />
        }
      />

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper transparent>
            <Box mb={10}>
              <Typography variant="h3" color={theme.palette.common.white}>
                <FormattedMessage id={`${PAGE_NAME_ID}.bestMixFeedDetails.title`} />
              </Typography>
            </Box>
            <Paper>
              <Grid container spacing={4}>
                <Grid size={6}>
                  <Typography variant="caption">
                    <FormattedMessage id={`${PAGE_NAME_ID}.bestMixFeedDetails.descriptionLabel`} />
                  </Typography>
                  <Typography>{bestMixFeed.description}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption">
                    <FormattedMessage id={`${PAGE_NAME_ID}.bestMixFeedDetails.plantCodeLabel`} />
                  </Typography>
                  <Typography>{bestMixFeed.plantCode}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption">
                    <FormattedMessage id={`${PAGE_NAME_ID}.bestMixFeedDetails.codeLabel`} />
                  </Typography>
                  <Typography>{bestMixFeed.code}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption">
                    <FormattedMessage id={`${PAGE_NAME_ID}.bestMixFeedDetails.bestMixFeedNumberLabel`} />
                  </Typography>
                  <Typography>{bestMixFeed.productNumber}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption">
                    <FormattedMessage id={`${PAGE_NAME_ID}.bestMixFeedDetails.formulationCostLabel`} />
                  </Typography>
                  <Typography>
                    <FormattedNumber value={bestMixFeed.productPricePerTon} style={`currency`} currency={bestMixFeed.priceUnitCode} />
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption">
                    <FormattedMessage id={`${PAGE_NAME_ID}.bestMixFeedDetails.bestMixFeedVersionLabel`} />
                  </Typography>
                  <Typography>{bestMixFeed.productVersion}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper transparent>
            <Box display="flex" justifyContent="space-between" alignItems="center" justifyItems={'center'} mb={3}>
              <Typography variant="h3" color={theme.palette.common.white}>
                <FormattedMessage id={`${PAGE_NAME_ID}.assignedCompaniesList.title`} />
              </Typography>
              <Dialog
                ref={linkCustomerDialogRef}
                maxWidth="sm"
                title={intl.formatMessage({ id: `${PAGE_NAME_ID}.assignedCompaniesDialog.title` })}
                button={
                  <Button disabled={!bestMixFeed.hasMandatoryNutrients}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.assignedCompaniesDialog.openBtn`} />
                  </Button>
                }>
                <form onSubmit={handleSubmit(handleUpdateBestMixFeedDetails)}>
                  <FlexBox>
                    {updateBestMixFeedDetails.isPending && <LoadingSpinner overlay />}
                    <Autocomplete
                      fullWidth
                      dark
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.assignedCompaniesDialog.customerLabel` })}
                      options={_.chain(customers)
                        .sortBy((c) => c.name.toLowerCase())
                        .filter((item) => !bestMixFeed.assignedCompaniesDetails.map((c) => c.companyId).includes(item.id))
                        .value()}
                      getOptionLabel={(option) => String((option as Customer).name)}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={String((option as Customer).id)}>
                            {String((option as Customer).name)}
                          </li>
                        );
                      }}
                      controller={{
                        name: 'customer',
                        control: control,
                        defaultValue: null,
                        rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                      }}
                    />
                    <TextField
                      dark
                      fullWidth
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.assignedCompaniesDialog.customerPricePerToneLabel` })}
                      controller={{
                        name: 'customerPricePerTone',
                        control: control,
                        rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                      }}
                    />
                    <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
                      <Button type="submit">
                        <FormattedMessage id="general.save" />
                      </Button>
                    </Box>
                  </FlexBox>
                </form>
              </Dialog>
            </Box>

            <List
              options={assignedCompaniesOptions}
              orderBy="name"
              isLoading={isLoading}
              noResult={{
                show: !bestMixFeed.assignedCompaniesDetails.length,
                text: <FormattedMessage id={`${PAGE_NAME_ID}.assignedCompaniesList.noResults`} />,
              }}>
              {bestMixFeed.assignedCompaniesDetails.map((row) => (
                <ListRow key={row.companyId}>
                  <ListCell align="left">{row.name}</ListCell>
                  <ListCell align="left">
                    <FormattedNumber value={row.customerPricePerTon} style={`currency`} currency={row.currencyCode} />
                  </ListCell>
                  <ListCell align="right">
                    <Button
                      onClick={() => handleUnlinkCustomer(row.companyId)}
                      size="small"
                      disabled={updateBestMixFeedDetails.isPending}>
                      <FormattedMessage id="general.delete" />
                    </Button>
                  </ListCell>
                </ListRow>
              ))}
            </List>
          </Paper>
        </Grid>
        <Grid size={12}>
          <Paper
            transparent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h3" color={theme.palette.common.white}>
                <FormattedMessage id={`${PAGE_NAME_ID}.customFieldsList.title`} />
              </Typography>
              <Dialog
                ref={customFieldsDialogRef}
                maxWidth="sm"
                title={intl.formatMessage({ id: `${PAGE_NAME_ID}.customFieldsDialog.title` }, { action: 'Add' })}
                button={
                  <Button disabled={updateBestMixFeedDetails.isPending}>
                    <Typography>
                      {intl.formatMessage({ id: `${PAGE_NAME_ID}.customFieldsDialog.openBtn` }, { action: 'Add' })}
                    </Typography>
                  </Button>
                }>
                <form onSubmit={handleSubmit(handleUpdateBestMixFeedDetails)}>
                  <FlexBox>
                    {updateBestMixFeedDetails.isPending && <LoadingSpinner overlay />}
                    <Autocomplete
                      dark
                      fullWidth
                      multiple={false}
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.customFieldsDialog.customFieldsLabel` })}
                      options={
                        customFields?.filter((item) => !bestMixFeed.customFields.map((cf) => cf.code).includes(item.code)) ?? []
                      }
                      getOptionLabel={(option) => String((option as CustomField).name)}
                      controller={{
                        name: 'customfields',
                        control: control,
                        defaultValue: null,
                        rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                      }}
                      disableCloseOnSelect={false}
                    />
                    <TextField
                      dark
                      fullWidth
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.customFieldsDialog.customFieldValueLabel` })}
                      controller={{
                        name: 'customfieldValue',
                        control: control,
                        rules: {
                          required: intl.formatMessage({ id: 'form.rules.required' }),
                          validate: {
                            positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                            between: (v) =>
                              parseFloat(v) <= 2.0
                                ? true
                                : intl.formatMessage({ id: 'form.rules.between' }, { lower: '0.0', upper: '2.0' }),
                          },
                        },
                      }}
                    />
                    <Box display={'flex'} justifyContent={'end'}>
                      <Button type="submit">
                        <FormattedMessage id="general.save" />
                      </Button>
                    </Box>    
                  </FlexBox>
                </form>
              </Dialog>
            </Box>

            <List
              options={customFieldsOptions}
              orderBy="name"
              isLoading={isLoading}
              noResult={{
                show: !bestMixFeed.customFields.length,
                text: <FormattedMessage id={`${PAGE_NAME_ID}.customFieldsList.noResults`} />,
              }}>
              {bestMixFeed.customFields.map((row, idx) => (
                <ListRow key={row.code}>
                  <ListCell align="left">{row.name}</ListCell>
                  <ListCell align="left">{row.value}</ListCell>
                  <ListCell align="right">
                    <Dialog
                      ref={(el) => {
                        if (el) return (editCustomFieldsDialogRef.current[idx] = el);
                      }}
                      maxWidth="sm"
                      title={`Delete (${row.name})`}
                      button={
                        <Button type="submit" size="small" title="Remove">
                          <FormattedMessage id="general.delete" />
                        </Button>
                      }>
                      <Box display={'flex'} flexDirection={'column'} gap={4}>
                        <Box>
                          <FormattedMessage id="general.deleteComment" />
                        </Box>
                        <Box display={'flex'} justifyContent={'end'} gap={4}>
                          <Button
                            variant="outlined"
                            type="submit"
                            size="small"
                            onClick={() => editCustomFieldsDialogRef.current[idx]?.close()}>
                            <FormattedMessage id="general.cancel" />
                          </Button>
                          <Button onClick={() => handleRemoveCustomField(row.code)} disabled={updateBestMixFeedDetails.isPending}>
                            <FormattedMessage id="general.delete" />
                          </Button>
                        </Box>
                      </Box>
                    </Dialog>
                  </ListCell>
                  <ListCell align="right">
                    <Dialog
                      maxWidth="sm"
                      ref={(el) => {
                        if (el) return (editCustomFieldsDialogRef.current[idx] = el);
                      }}
                      title={intl.formatMessage({ id: `${PAGE_NAME_ID}.customFieldsDialog.title` }, { action: 'Edit' })}
                      button={
                        <Button
                          disabled={updateBestMixCustomField.isPending}
                          sx={{ position: 'right' }}
                          size="small"
                          onClick={() => {
                            setDialogIndex(idx);
                            reset({ customfieldValue: row.value });
                          }}>
                          <Typography>{intl.formatMessage({ id: `general.edit` }, { type: '' })}</Typography>
                        </Button>
                      }>
                      <form onSubmit={handleSubmit(handleUpdateCustomField)}>
                        <FlexBox>
                          {updateBestMixCustomField.isPending && <LoadingSpinner overlay />}
                          <TextField
                            label={'code'}
                            sx={{ display: 'none' }}
                            controller={{
                              name: 'customfieldCode',
                              control: control,
                              defaultValue: row.code,
                            }}
                          />
                          <TextField
                            dark
                            fullWidth
                            disabled
                            controller={{
                              name: 'customFieldName',
                              control: control,
                              defaultValue: row.name,
                            }}
                            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.customFieldsDialog.customFieldsLabel` })}
                          />
                          <TextField
                            dark
                            type="number"
                            fullWidth
                            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.customFieldsDialog.customFieldValueLabel` })}
                            controller={{
                              name: 'customfieldValue',
                              control: control,
                              rules: {
                                required: intl.formatMessage({ id: 'form.rules.required' }),
                                validate: {
                                  positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                                  between: (v) =>
                                    parseFloat(v) <= 2.0
                                      ? true
                                      : intl.formatMessage({ id: 'form.rules.between' }, { lower: '0.0', upper: '2.0' }),
                                },
                              },
                            }}
                          />
                          <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={4}>
                            <Button type="submit" size="medium">
                              <FormattedMessage id="general.save" />
                            </Button>
                          </Box>
                        </FlexBox>
                      </form>
                    </Dialog>
                  </ListCell>
                </ListRow>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Collapsible open label={intl.formatMessage({ id: `${PAGE_NAME_ID}.nutrientsMaterialsList.nutrientsTitle` })}>
            <Box display={'flex'} flexDirection={'column'} gap={4}>
              <FlexBox flexDirection={'row'} justifyContent={'end'}>
                <TextField
                  dark
                  label={intl.formatMessage({ id: 'general.search' })}
                  size="small"
                  value={NutrientsSearchValue}
                  endAdornment={<Search size={18} />}
                  onChange={(event) => {
                    setNutrientsSearchValue(event.target.value);
                  }}
                />
              </FlexBox>

              <Box overflow={'auto'} maxHeight={800}>
                <List
                  dark
                  options={NutrientsListOptions}
                  orderBy="name"
                  isLoading={isLoading}
                  noResult={{
                    show: !bestMixFeed.nutrients.length,
                    text: <FormattedMessage id={`${PAGE_NAME_ID}.nutrientsMaterialsList.noResults`} />,
                  }}>
                  {_.sortBy(bestMixFeed.nutrients, 'nutrientOrder')
                    .filter((row) => !!row.nutrientCode)
                    .filter(
                      (row) =>
                        row.nutrientCode.toLowerCase().includes(NutrientsSearchValue.toLowerCase()) ||
                        row.nutrientDescription.toLowerCase().includes(NutrientsSearchValue.toLowerCase())
                    )
                    .map((row) => (
                      <ListRow key={row.nutrientCode}>
                        <WrappedWordCell align="left" maxWidth={10}>
                          {row.nutrientCode}
                        </WrappedWordCell>
                        <WrappedWordCell align="left" maxWidth={20}>
                          {row.nutrientDescription}
                        </WrappedWordCell>
                        <WrappedWordCell align="right" maxWidth={10}>{`${row.nutrientValue} ${row.unitCode ?? ''}`}</WrappedWordCell>
                      </ListRow>
                    ))}
                </List>
              </Box>
            </Box>
          </Collapsible>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Collapsible open label={intl.formatMessage({ id: `${PAGE_NAME_ID}.nutrientsMaterialsList.materialsTitle` })}>
            <Box display={'flex'} flexDirection={'column'} gap={4}>
              <FlexBox flexDirection={'row'} justifyContent={'end'}>
                <TextField
                  dark
                  label={intl.formatMessage({ id: 'general.search' })}
                  size="small"
                  value={materialsSearchValue}
                  endAdornment={<Search size={18} />}
                  onChange={(event) => {
                    setMaterialsSearchValue(event.target.value);
                  }}
                />
              </FlexBox>
              <Box overflow={'auto'} maxHeight={800}>
                <List
                  dark
                  options={NutrientsListOptions}
                  orderBy="name"
                  isLoading={isLoading}
                  noResult={{
                    show: !bestMixFeed.materials.length,
                    text: <FormattedMessage id={`${PAGE_NAME_ID}.nutrientsMaterialsList.noResults`} />,
                  }}>
                  {_.sortBy(bestMixFeed.materials, 'materialOrder')
                    .filter((row) => !!row.materialCode)
                    .filter(
                      (row) =>
                        row.materialCode.toLowerCase().includes(materialsSearchValue.toLowerCase()) ||
                        row.materialDescription.toLowerCase().includes(materialsSearchValue.toLowerCase())
                    )
                    .map((row) => (
                      <ListRow key={row.materialCode}>
                        <WrappedWordCell align="left" maxWidth={10}>
                          {row.materialCode}
                        </WrappedWordCell>
                        <WrappedWordCell align="left" maxWidth={20}>
                          {row.materialDescription}
                        </WrappedWordCell>
                        <WrappedWordCell align="right" maxWidth={10}>{`${row.weight} ${row.weightUnitCode}`}</WrappedWordCell>
                      </ListRow>
                    ))}
                </List>
              </Box>
            </Box>
          </Collapsible>
        </Grid>
      </Grid>
    </>
  );
};

export default EditBestMixFeed;
